import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getPlcCapabilities, getPlcFieldbusProbes } from "../../../api";
import GatewayTabs from "../../../components/GatewayTabs";
import {
  canConfigureRoadmapItem,
  normalizePlcRoadmap,
} from "./plc-capabilities-model";
import { PlcStatusPill } from "./plc-status-pill";

// Health-style catalogs: tone reflects whether the thing actually works.
const STATUS = {
  available: { label: "Available", tone: "positive" },
  configuration_only: { label: "Configuration only", tone: "neutral" },
  in_progress: { label: "In progress", tone: "warning" },
  lab_gated: { label: "Lab gated", tone: "warning" },
  release_gated: { label: "Release gated", tone: "danger" },
  planned: { label: "Planned", tone: "neutral" },
};

const READINESS = {
  ready: { label: "Ready", tone: "positive" },
  host_blocked: { label: "Host blocked", tone: "warning" },
  release_blocked: { label: "Release blocked", tone: "warning" },
  not_implemented: { label: "Backend required", tone: "neutral" },
};

const CHECK = {
  pass: { label: "Pass", tone: "positive" },
  fail: { label: "Blocked", tone: "danger" },
  warning: { label: "Warning", tone: "warning" },
  unknown: { label: "Unknown", tone: "neutral" },
};

const RELEASE_GATE = {
  pass: { label: "Passed", tone: "positive" },
  passed: { label: "Passed", tone: "positive" },
  complete: { label: "Complete", tone: "positive" },
  verified: { label: "Verified", tone: "positive" },
  fail: { label: "Blocked", tone: "danger" },
  failed: { label: "Blocked", tone: "danger" },
  blocked: { label: "Blocked", tone: "danger" },
  warning: { label: "Warning", tone: "warning" },
  pending: { label: "Pending", tone: "warning" },
  required: { label: "Required", tone: "warning" },
  not_applicable: { label: "Not applicable", tone: "neutral" },
  unknown: { label: "Unknown", tone: "neutral" },
};

// Stage/maturity catalogs describe sequencing, not health. Keep them mostly
// neutral and let the label do the differentiating; only pull out values
// that clearly signal a warning-grade caveat.
const VALIDATION = {
  software_tested: { label: "Software tested", tone: "neutral" },
  hardware_lab_required: { label: "Hardware lab required", tone: "warning" },
  roadmap: { label: "Roadmap", tone: "neutral" },
};

const STAGE = {
  delivered: { label: "Delivered", tone: "neutral" },
  active: { label: "Active", tone: "warning" },
  planned: { label: "Planned", tone: "neutral" },
  gated: { label: "Gated", tone: "neutral" },
};

const MATURITY = {
  operational: { label: "Operational", tone: "positive" },
  release_validation: { label: "Release validation", tone: "warning" },
  foundation: { label: "Foundation", tone: "neutral" },
  specified: { label: "Specified", tone: "neutral" },
  backlog: { label: "Backlog", tone: "neutral" },
};

const humanize = (value) =>
  String(value || "Unknown")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const configurationModeCopy = (mode) => {
  if (mode === "available") return "Configuration is available.";
  if (mode === "configuration_only") {
    return "Configuration can be stored, but it does not execute on this target.";
  }
  if (mode === "not_available") return "Configuration is not available.";
  return mode ? `${humanize(mode)} mode.` : "";
};

const fieldbusProtocolLabel = (protocol) => {
  if (protocol === "ethernet_ip_class1") return "EtherNet/IP Class 1";
  if (protocol === "ethercat") return "EtherCAT";
  if (protocol === "profinet_io_controller") return "PROFINET IO Controller";
  if (protocol === "profibus_dp") return "PROFIBUS DP";
  return protocol ? String(protocol) : "Unknown protocol";
};

const Badge = ({ value, catalog }) => {
  const entry = catalog[value] || { label: humanize(value), tone: "neutral" };
  return <PlcStatusPill tone={entry.tone}>{entry.label}</PlcStatusPill>;
};

const CAPABILITY_VIEWS = [
  { id: "current", label: "Current" },
  { id: "roadmap", label: "Roadmap" },
  { id: "all", label: "All" },
];

const firstOpenGate = (item) => (item?.releaseGates || []).find((gate) =>
  ["pending", "blocked", "failed", "required"].includes(String(gate?.status || "").toLowerCase())
);

export const PlcCapabilitiesPage = () => {
  const [manifest, setManifest] = useState(null);
  const [fieldbus, setFieldbus] = useState(null);
  const [error, setError] = useState("");
  const [fieldbusError, setFieldbusError] = useState("");
  const [fieldbusLoading, setFieldbusLoading] = useState(true);
  const [capabilityView, setCapabilityView] = useState("current");
  const mountedRef = useRef(true);
  const fieldbusGenerationRef = useRef(0);

  const loadFieldbus = useCallback(async () => {
    const generation = ++fieldbusGenerationRef.current;
    if (mountedRef.current) {
      // Readiness is safety-sensitive. Never keep rendering a previously
      // successful "Ready" result while a refresh is pending or has failed.
      setFieldbus(null);
      setFieldbusLoading(true);
      setFieldbusError("");
    }
    try {
      const next = await getPlcFieldbusProbes();
      if (!mountedRef.current || generation !== fieldbusGenerationRef.current) return;
      setFieldbus(next);
    } catch (reason) {
      if (!mountedRef.current || generation !== fieldbusGenerationRef.current) return;
      setFieldbusError(String(
        reason?.message || reason || "Could not inspect fieldbus readiness."
      ));
    } finally {
      if (mountedRef.current && generation === fieldbusGenerationRef.current) {
        setFieldbusLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    getPlcCapabilities()
      .then((next) => {
        if (!cancelled) {
          setManifest(next);
          setError("");
        }
      })
      .catch((reason) => {
        if (!cancelled)
          setError(
            String(
              reason?.message || reason || "Could not load PLC capabilities."
            )
          );
      });
    loadFieldbus();
    return () => {
      cancelled = true;
      mountedRef.current = false;
      fieldbusGenerationRef.current += 1;
    };
  }, [loadFieldbus]);

  const roadmap = useMemo(() => normalizePlcRoadmap(manifest), [manifest]);
  const visibleCategories = useMemo(() => roadmap.categories
    .map((category) => ({
      ...category,
      items: category.items.filter((item) => {
        if (capabilityView === "roadmap") return item.status === "planned";
        if (capabilityView === "current") return item.status !== "planned";
        return true;
      }),
    }))
    .filter((category) => category.items.length > 0), [capabilityView, roadmap.categories]);
  const capabilityTabs = useMemo(() => CAPABILITY_VIEWS.map((item) => ({
    ...item,
    description: item.id === "current"
      ? `${Math.max(0, roadmap.counts.total - roadmap.counts.planned)} items`
      : item.id === "roadmap"
        ? `${roadmap.counts.planned} items`
        : `${roadmap.counts.total} items`,
  })), [roadmap.counts]);

  const targetLabel =
    manifest?.target === "cloud_control_plane"
      ? "Cloud control plane"
      : manifest?.target === "local_edge_runtime"
        ? "Local / edge runtime"
        : "Unknown runtime target";
  const scopeLabel = manifest?.scope?.kind === "host_build"
    ? `${manifest.scope.host_os || "unknown"}/${manifest.scope.host_architecture || "unknown"} serving host`
    : "";

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Compatibility</div>
            <div className="gwPageSubtitle">
              {manifest
                ? `${targetLabel}${scopeLabel ? ` · ${scopeLabel}` : ""} · truthful runtime and validation status`
                : error
                  ? "Capability manifest unavailable"
                  : "Loading target capabilities…"}
            </div>
          </div>
        </div>

        <div className="gwNotice isSoft">
          <strong>
            A configuration screen is not a protocol-support claim.
          </strong>{" "}
          Available means this build has an executable path. Hardware lab
          required means the implementation still needs physical
          interoperability and soak evidence before a production-grade claim.
          Planned features cannot execute; an item may expose a clearly marked,
          non-executing configuration foundation when that workflow already exists.
        </div>

        {Array.isArray(manifest?.warnings) && manifest.warnings.length > 0 ? (
          <div className="plcRoadmapWarnings" role="note">
            <strong>Capability contract</strong>
            <ul>
              {manifest.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {manifest ? (
          <div className="plcRoadmapStats" aria-label="PLC roadmap summary">
            <div className="plcRoadmapStat">
              <span>Executable now</span>
              <strong>{roadmap.counts.available || 0}</strong>
            </div>
            <div className="plcRoadmapStat">
              <span>Planned</span>
              <strong>{roadmap.counts.planned || 0}</strong>
            </div>
            <div className="plcRoadmapStat">
              <span>Lab gated</span>
              <strong>{roadmap.counts.lab_gated || 0}</strong>
            </div>
            <div className="plcRoadmapStat">
              <span>Total roadmap items</span>
              <strong>{roadmap.counts.total || 0}</strong>
            </div>
          </div>
        ) : null}

        {error && (
          <div className="gwAlert" role="alert">
            <div className="gwAlertTitle">PLC capabilities unavailable</div>
            <div className="gwAlertBody">{error}</div>
          </div>
        )}

        <div className="gwCardSection" data-gw-card="plc_fieldbus_readiness">
          <div className="gwSectionTitleRow gwSectionTitle">
            <span>Fieldbus readiness</span>
            <button
              type="button"
              className="gwGhostBtn"
              disabled={fieldbusLoading}
              onClick={loadFieldbus}
            >
              {fieldbusLoading ? "Refreshing…" : "Refresh readiness"}
            </button>
          </div>
          <div className="gwCardSectionBody">
            <div className="gwHint">
              These are live prerequisites and release gates. They do not send fieldbus frames or imply protocol support.
            </div>
            {fieldbusError ? (
              <div className="gwAlert" role="alert">
                <div className="gwAlertTitle">Fieldbus inspection unavailable</div>
                <div className="gwAlertBody">{fieldbusError}</div>
              </div>
            ) : null}
            {fieldbusLoading && !fieldbus ? <div className="gwHint">Checking this runtime…</div> : null}
            {fieldbus?.host ? (
              <div className="gwNotice isSoft">
                <strong>{fieldbus.host.os} / {fieldbus.host.architecture}</strong>{" "}
                · {(fieldbus.host.ethernet_interfaces || []).length} candidate Ethernet interface
                {(fieldbus.host.ethernet_interfaces || []).length === 1 ? "" : "s"}
                {(fieldbus.host.ethernet_interfaces || []).length > 0
                  ? ` (${fieldbus.host.ethernet_interfaces.map((item) => item.name).join(", ")})`
                  : ""}
              </div>
            ) : null}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(fieldbus?.protocols || []).map((protocol) => (
                <div className="umUserRow" key={protocol.protocol} style={{ alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span className="umUserName">
                        {fieldbusProtocolLabel(protocol.protocol)}
                      </span>
                      <Badge value={protocol.readiness} catalog={READINESS} />
                    </div>
                    <div className="umUserMeta" style={{ marginTop: 5, whiteSpace: "normal" }}>
                      {protocol.execution_available && protocol.readiness === "ready"
                        ? "An approved executable backend and qualified adapter are available on this runtime."
                        : "Execution is locked until every required host, backend, release, and hardware gate passes."}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 8 }}>
                      {(protocol.checks || []).map((check) => (
                        <div key={check.code} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                          <Badge value={check.status} catalog={CHECK} />
                          <div className="umUserMeta" style={{ lineHeight: 1.4, whiteSpace: "normal" }}>
                            <strong style={{ color: "var(--gw-text-secondary)" }}>{check.label}</strong>
                            {check.detail ? ` · ${check.detail}` : ""}
                            {check.remediation ? <div>{check.remediation}</div> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                    {(protocol.backends || []).length === 0 ? (
                      <div className="gwHint" style={{ marginTop: 8 }}>
                        No executable backend is registered in this build.
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {manifest && roadmap.phases.length > 0 ? (
          <div className="gwCardSection" data-gw-card="plc_roadmap_phases">
            <div className="gwSectionTitle">Delivery phases</div>
            <div className="gwCardSectionBody">
              <div className="gwHint">
                Phases communicate sequencing, not promised release dates. Dependencies and release evidence still control availability.
              </div>
              <div className="plcRoadmapPhaseGrid">
                {roadmap.phases.map((phase) => (
                  <div className="plcRoadmapPhaseCard" key={phase.id}>
                    <div className="plcRoadmapPhaseHeading">
                      <div className="plcRoadmapPhaseName">
                        <strong>{phase.title}</strong>
                        {phase.status ? <Badge value={phase.status} catalog={STAGE} /> : null}
                      </div>
                      <span>{phase.count} item{phase.count === 1 ? "" : "s"}</span>
                    </div>
                    {phase.summary ? <p>{phase.summary}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {manifest ? (
          <div className="plcRoadmapHeading gwFormStack">
            <div>
              <div className="gwPageTitle">Capability catalog</div>
              <div className="gwPageSubtitle">
                Current behavior is the default view. Roadmap items are separately gated work, not promises that this runtime can execute them.
              </div>
            </div>
            <GatewayTabs
              items={capabilityTabs}
              active={capabilityView}
              onChange={setCapabilityView}
              ariaLabel="Capability catalog view"
              idPrefix="plc-capability-view"
              panelIdPrefix="plc-capability-view"
            />
          </div>
        ) : null}

        {manifest ? (
          <div
            className="gwFormStack"
            id={`plc-capability-view-panel-${capabilityView}`}
            role="tabpanel"
            aria-labelledby={`plc-capability-view-${capabilityView}`}
          >
          {visibleCategories.map((category) => (
            <div className="gwCardSection" key={category.id} data-roadmap-area={category.id}>
            <div className="gwSectionTitleRow gwSectionTitle">
              <span>{category.title}</span>
              <span className="plcRoadmapCount">
                {category.items.length} item{category.items.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="gwCardSectionBody plcRoadmapCategoryBody">
              {category.summary ? (
                <div className="plcRoadmapCategoryIntro">{category.summary}</div>
              ) : null}
              {category.items.length === 0 ? (
                <div className="plcRoadmapEmpty">
                  No work items in this runtime manifest yet.
                </div>
              ) : category.items.map((item) => {
                const blocker = item.status === "planned" ? firstOpenGate(item) : null;
                return (
                  <article className="plcRoadmapItem" key={item.id}>
                  <div className="plcRoadmapItemHeader">
                    <div className="plcRoadmapItemTitle">
                      <strong>{item.title}</strong>
                      {item.role ? <span>{item.role}</span> : null}
                    </div>
                    <div className="plcRoadmapBadges">
                      <Badge value={item.status} catalog={STATUS} />
                      <Badge value={item.validation} catalog={VALIDATION} />
                      {item.maturity ? <Badge value={item.maturity} catalog={MATURITY} /> : null}
                      <span className="plcRoadmapPhaseBadge">{item.phase.title}</span>
                    </div>
                  </div>

                  {item.summary ? <p className="plcRoadmapSummary">{item.summary}</p> : null}

                  {blocker ? (
                    <div className="plcRoadmapConfigure">
                      <strong>Why it is not available:</strong>{" "}
                      {blocker.label}{blocker.detail ? ` · ${blocker.detail}` : ""}
                    </div>
                  ) : null}

                  {item.configure ? (
                    <div className={`plcRoadmapConfigure${canConfigureRoadmapItem(item) ? " isAvailable" : ""}`}>
                      <strong>
                        {item.status === "available"
                          ? "Available in"
                          : item.status === "configuration_only" && canConfigureRoadmapItem(item)
                            ? "Configuration only in"
                            : canConfigureRoadmapItem(item)
                              ? "Configuration in"
                              : "Planned surface"}:
                      </strong>{" "}
                      {item.configure}
                      {item.configureMode ? ` · ${configurationModeCopy(item.configureMode)}` : ""}
                      {!canConfigureRoadmapItem(item) ? " · No configuration is enabled yet." : ""}
                    </div>
                  ) : null}

                  {item.dependencies.length + item.releaseGates.length + item.limitations.length > 0 ? (
                    <details className="plcRoadmapEvidence">
                      <summary>
                        Dependencies and release evidence
                        <span>
                          {item.dependencies.length + item.releaseGates.length + item.limitations.length} detail
                          {item.dependencies.length + item.releaseGates.length + item.limitations.length === 1 ? "" : "s"}
                        </span>
                      </summary>
                      <div className="plcRoadmapEvidenceBody">
                        {item.dependencies.length > 0 ? (
                          <div className="plcRoadmapDetail">
                            <strong>Dependencies</strong>
                            <div className="plcRoadmapChips">
                              {item.dependencies.map((dependency) => (
                                <span className="plcRoadmapChip" key={dependency.id}>
                                  {dependency.label}
                                  {dependency.status ? ` · ${humanize(dependency.status)}` : ""}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {item.releaseGates.length > 0 ? (
                          <div className="plcRoadmapDetail">
                            <strong>Release gates</strong>
                            <div className="plcRoadmapGateList">
                              {item.releaseGates.map((gate) => (
                                <div className="plcRoadmapGate" key={gate.id}>
                                  <Badge value={gate.status} catalog={RELEASE_GATE} />
                                  <span>
                                    {gate.label}
                                    {!gate.required ? " · Optional" : ""}
                                    {gate.detail ? ` · ${gate.detail}` : ""}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {item.limitations.length > 0 ? (
                          <div className="plcRoadmapDetail">
                            <strong>Current limits</strong>
                            <ul className="plcRoadmapLimitations">
                              {item.limitations.map((limitation) => (
                                <li key={limitation}>{limitation}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                  </article>
                );
              })}
            </div>
          </div>
          ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};
